<template>
  <div>
    <h1>
      <Icon
        name="Privacy Primary"
        class="inline-block h-[1em] w-[1em] shrink-0 text-start align-middle text-primary"
      />
      {{ $t("pages.account.reset-password.title") }}
    </h1>

    <section class="mt-10 bg-[#F0FBFB] p-4 py-14">
      <div class="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
        <div class="rounded-sm bg-white p-7 md:p-7 md:shadow-md">
          <CommonAlert v-if="actionState" :type="actionState.status" :content="actionState.message" class="mb-6" />
          <p class="mt-6">
            {{ $t("pages.account.reset-password.texts.description") }}
          </p>

          <VForm :validation-schema="Schema" :initial-values="Schema.getDefault()" v-slot="form" as="div"
            class="mt-6 w-full">
            <form @submit="form.handleSubmit($event, mutate)" class="mx-auto">
              <div class="-mt-2 w-full md:mt-0">
                <div class="flex flex-col md:gap-4 md:pt-4">
                  <BaseVTextInput
                    type="email"
                    name="email"
                    :label="`${$t('common.fields.email')}`"
                  />
                  <div class="mt-2">
                    <BaseVTextInput
                      type="password"
                      :label="`${$t('common.fields.password')}`"
                      name="password"
                    />
                    <span class="mt-2 flex flex-row items-center gap-2 text-sm">
                      <CInfoE class="text-[1em]" />
                      {{
                        $t("pages.account.signup.texts.password_requirements")
                      }}
                    </span>
                  </div>

                  <BaseVTextInput
                    type="password"
                    :label="`${$t('common.fields.password_confirmation')}`"
                    name="password_confirmation"
                    class="mt-3"
                  />
                </div>

                <BaseButton
                  type="submit"
                  :text="`${$t('pages.account.reset-password.buttons.reset_password')}`"
                  class="mt-4 h-10 w-fit text-[1rem]"
                  :loading="pending"
                  :disabled="!canExecute"
                />
              </div>
            </form>
          </VForm>
        </div>

        <CommonLookingForTestResultCard />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import * as yup from "yup";
import CInfoE from "~/components/icons/CIconE.vue";
const { t } = useI18n();

interface PasswordResetResponse {
  status: "success" | "error";
  message: string;
}
const actionState = ref<PasswordResetResponse>();

const Schema = yup.object({
  email: yup.string().email().required().default(""),
  password: yup.string().required().default(""),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], t("pages.account.signup.errors.passwords_mismatch"))
    .required()
    .default(""),
});
type Schema = yup.InferType<typeof Schema>;

const route = useRoute();
const { pending, mutate, canExecute } = useMutation({
  executionInterval: 5,
  executionIntervalUnit: "seconds",
  mutationFn: (credentials: Schema) => {
    const token = route.query.token;

    const payload = {
      ...credentials,
      token,
    };

    return useAxios().postForm<{ status: boolean }>(`/password/reset`, payload);
  },
  onError(err) {
    actionState.value = {
      status: "error",
      message: err?.response?.data?.message || err.message,
    };
  },
  async onSuccess({ data }) {
    actionState.value = {
      status: data.status ? "success" : "error",
      message: t("pages.account.reset-password.texts.success"),
    };

    const localePath = useLocalePath();
    await navigateTo(
      localePath({
        path: "account/login",
      }),
    );
  },
});
</script>
